import { AfterViewInit, Component, EventEmitter, OnInit } from '@angular/core';
import { ECharts, EChartsOption } from 'echarts';
import { IDateRangePickerDashboardItem } from 'src/app/components/date-picker/date-picker.component';
import { IMultipleSelectDashboardItem } from 'src/app/components/multiple-select/multiple-select.component';
import { ChartColorService } from 'src/app/services/chart-color.service';
import { CommonLegendService } from 'src/app/services/common-legend.service';
import { IDashboardItemDataLoaderLink } from 'src/app/services/dashboard-item/IDashboardItemDataLoaderLink';
import { DashboardService } from 'src/app/services/dashboard/dashboard.service';
import { IDataLoaderResult } from 'src/app/services/data-loader/data-loader-result';
import { DataLoaderFactories } from 'src/app/services/data-loader/data-loader.service';
import {
    IQueryContext,
    QueryContextSettingID,
} from 'src/app/services/query-context/models/IQueryContext';
import { QueryContextService } from 'src/app/services/query-context/query-context.service';
import {
    DatasourceColumnId,
    datasourceColumnOrderByQuery,
} from 'src/app/utils/datasource-sql-map/datasource';
import { EChartsAxisHelper } from 'src/app/utils/echarts/echarts-axis-helper';
import { EChartsHelper } from 'src/app/utils/echarts/echarts-helper';
import { EChartsTooltipHelper } from 'src/app/utils/echarts/echarts-tooltip-helper';
import { defaultColorGetterBuilder } from 'src/app/utils/echarts/series-color';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { currentDataProject } from 'src/app/services/data-project/models/data-project.model';
import { datePartDateTrunc, getAllDatePartDateTrunc } from 'src/app/utils/sql';
import { DatasourceColumnOldDataLoaderFactoryOptions } from '../common/data-loaders/datasource-column-old';
import { SegmentDatasourceColumnDataLoaderFactoryOptions } from '../common/data-loaders/segments';
import { DashboardItemFactories } from '../common/items/common-dashboard-items';
import { ModifyTimeDashboardItemFactoryOptions } from '../common/items/modify-time';
import { ICheckboxSettingDashboardItem } from '../common/items/setting/checkbox/checkbox.component';
import { ISingleSelectSettingDashboardItem } from '../common/items/setting/single-select/single-select.component';
import { IToggleSettingDashboardItem } from '../common/items/setting/toggle/toggle.component';
import { StandardDashboardWorker } from '../common/standard-dashboard';
import { RevenueARPUDataLoaderFactory } from './data-loaders/revenue-arpu';
import { RevenueDauDataLoaderFactory } from './data-loaders/revenue-dau';
import { SalesComparisonDataLoaderFactory } from './data-loaders/sales-comparison';
import { MopaSalesChartDocs } from './mopa-sales.chart-documentation';
import { BIVizDashboardSales } from './sql-maps/biviz-dashboard-sales-datasource-sql-map';

@Component({
    selector: 'app-mopa-sales',
    templateUrl: './mopa-sales.component.html',
    styleUrls: ['./mopa-sales.component.scss'],
    providers: [DashboardService, QueryContextService, CommonLegendService],
})
export class MopaSalesComponent
    extends StandardDashboardWorker
    implements OnInit, AfterViewInit
{
    override DASHBOARD_NAME = 'Sales';
    override DASHBOARD_ID = 'mopa-sales';
    values = Object.values;

    private gcpProjectId: GcpProjectId = currentDataProject().gcpProjectId;

    /////////////////////////////////////////////////////////////////
    ////
    ////    DATALOADERS
    ////    Some DataLoader can be used in multiple charts.
    ////    That's why there are created here and later referenced
    ////
    /////////////////////////////////////////////////////////////////

    override filterDataLoaders = {
        'mopa.sales.filter.inapp_source': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.sales.filter.inapp_source',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'SALES_INAPP_SOURCE',
            datasourceSqlMap: BIVizDashboardSales.sqlMap,
            filterContext: true,
        }).create(),
        'mopa.sales.filter.offer_name': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.sales.filter.offer_name',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'SALES_OFFER_NAME',
            datasourceSqlMap: BIVizDashboardSales.sqlMap,
            filterContext: true,
        }).create(),
        'mopa.sales.filter.offer_type': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.sales.filter.offer_type',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'SALES_OFFER_TYPE',
            datasourceSqlMap: BIVizDashboardSales.sqlMap,
            filterContext: true,
        }).create(),
        'mopa.sales.filter.offer_segment': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.sales.filter.offer_segment',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'SALES_OFFER_SEGMENT',
            datasourceSqlMap: BIVizDashboardSales.sqlMap,
            filterContext: true,
        }).create(),
        'mopa.sales.filter.offer_season_variant': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.sales.filter.offer_season_variant',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'SALES_OFFER_SEASON_VARIANT',
            datasourceSqlMap: BIVizDashboardSales.sqlMap,
            filterContext: true,
            orderByQuery: datasourceColumnOrderByQuery(
                'SALES_OFFER_SEASON_VARIANT'
            ),
        }).create(),
        'mopa.sales.filter.offer_season_size': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.sales.filter.offer_season_size',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'SALES_OFFER_SEASON_SIZE',
            datasourceSqlMap: BIVizDashboardSales.sqlMap,
            filterContext: true,
            orderByQuery: datasourceColumnOrderByQuery(
                'SALES_OFFER_SEASON_SIZE'
            ),
        }).create(),
        'mopa.sales.filter.inapp_product_id': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.sales.filter.inapp_product_id',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'SALES_INAPP_PRODUCT_ID',
            datasourceSqlMap: BIVizDashboardSales.sqlMap,
            filterContext: true,
        }).create(),
        'mopa.sales.filter.inapp_product_id_price_tier': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.sales.filter.inapp_product_id_price_tier',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'SALES_INAPP_PRODUCT_ID_PRICE_TIER',
            datasourceSqlMap: BIVizDashboardSales.sqlMap,
            filterContext: true,
            orderByQuery: datasourceColumnOrderByQuery(
                'SALES_INAPP_PRODUCT_ID_PRICE_TIER'
            ),
        }).create(),
        'mopa.sales.filter.sales_inapp_transactions_no_bucket':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'mopa.sales.filter.sales_inapp_transactions_no_bucket',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'SALES_INAPP_TRANSACTION_NO_BUCKET',
                datasourceSqlMap: BIVizDashboardSales.sqlMap,
                orderByDatasourceColumnIds: [
                    'SALES_INAPP_TRANSACTION_NO_BUCKET_SORT_KEY',
                ],
            }).create(),
        'mopa.sales.filter.sales_inapp_transactions_no_label':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'mopa.sales.filter.sales_inapp_transactions_no_label',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'SALES_INAPP_TRANSACTION_NO_LABEL',
                datasourceSqlMap: BIVizDashboardSales.sqlMap,
                orderByQuery: datasourceColumnOrderByQuery(
                    'SALES_INAPP_TRANSACTION_NO_LABEL'
                ),
            }).create(),
        // 'mopa.sales.filter.inapp_product_name': DataLoaderFactories[
        //     'common.filter.datasource_column'
        // ]({
        //     createdInstanceId: 'mopa.sales.filter.inapp_product_name',
        //     gcpProjectId: this.gcpProjectId,
        //     queryCacheService: this.queryCache,
        //     datasourceColumnId: 'SALES_INAPP_PRODUCT_NAME',
        //     datasourceSqlMap: BIVizDashboardSales.sqlMap,
        // }).create(),
        'mopa.sales.filter.ad_platform': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.sales.filter.ad_platform',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'AD_PLATFORM',
            datasourceSqlMap: BIVizDashboardSales.sqlMap,
        }).create(),
        'mopa.sales.filter.segment': DataLoaderFactories[
            'common.filter.segment'
        ](
            new SegmentDatasourceColumnDataLoaderFactoryOptions(
                'mopa.sales.filter_segments',
                this.gcpProjectId,
                this.queryCache
            )
        ).create(),
        'mopa.sales.filter.activity_app_version': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.sales.filter.app_version',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'ACTIVITY_APP_VERSION',
            datasourceSqlMap: BIVizDashboardSales.sqlMap,
            orderByQuery: datasourceColumnOrderByQuery('ACTIVITY_APP_VERSION'),
        }).create(),
        'mopa.sales.filter.activity_app_version_group_minor':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'mopa.sales.filter.activity_app_version_group_minor',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'ACTIVITY_APP_VERSION_GROUP_MINOR',
                datasourceSqlMap: BIVizDashboardSales.sqlMap,
                orderByQuery: datasourceColumnOrderByQuery(
                    'ACTIVITY_APP_VERSION_GROUP_MINOR'
                ),
            }).create(),
        'mopa.sales.filter.activity_day_no': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.sales.filter.activity_day_no',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'ACTIVITY_DAY_NO',
            datasourceSqlMap: BIVizDashboardSales.sqlMap,
            additionalSQLTokens: {
                where: ['day_no <= 180'],
            },
        }).create(),
        'mopa.sales.filter.daily_total_energy_spent': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.sales.filter.daily_total_energy_spent',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'DAILY_TOTAL_ENERGY_SPENT',
            datasourceSqlMap: BIVizDashboardSales.sqlMap,
            filterContext: true,
        }).create(),
        'mopa.sales.filter.firebase_app_store': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.sales.filter.firebase_app_store',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'FIREBASE_APP_STORE',
            datasourceSqlMap: BIVizDashboardSales.sqlMap,
        }).create(),
        'mopa.sales.filter.firebase_country_european_union':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'mopa.sales.filter.firebase_country_european_union',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'FIREBASE_COUNTRY_EUROPEAN_UNION',
                datasourceSqlMap: BIVizDashboardSales.sqlMap,
            }).create(),
        'mopa.sales.filter.firebase_country_tier': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.sales.filter.firebase_country_tier',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'FIREBASE_COUNTRY_TIER',
            datasourceSqlMap: BIVizDashboardSales.sqlMap,
        }).create(),
        'mopa.sales.filter.firebase_country_is_relevant_label':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'mopa.sales.filter.firebase_country_is_relevant_label',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'FIREBASE_COUNTRY_IS_RELEVANT_LABEL',
                datasourceSqlMap: BIVizDashboardSales.sqlMap,
            }).create(),
        'mopa.sales.filter.firebase_country': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.sales.filter.firebase_country',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'FIREBASE_COUNTRY',
            datasourceSqlMap: BIVizDashboardSales.sqlMap,
            filterContext: ['FIREBASE_COUNTRY_TIER'],
        }).create(),
        'mopa.sales.filter.firebase_country_us_region': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.sales.filter.firebase_country_us_region',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'FIREBASE_COUNTRY_US_REGION',
            datasourceSqlMap: BIVizDashboardSales.sqlMap,
        }).create(),
        'mopa.sales.filter.attribution_traffic_source': DataLoaderFactories[
            'common.filter.datasource_column'
        ](
            new DatasourceColumnOldDataLoaderFactoryOptions({
                createdInstanceId:
                    'mopa.sales.filter.attribution_traffic_source',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'ATTRIBUTION_TRAFFIC_SOURCE',
                datasourceSqlMap: BIVizDashboardSales.sqlMap,
            })
        ).create(),
        'mopa.sales.filter.firebase_install_version': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.sales.filter.firebase_install_version',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'FIREBASE_INSTALL_VERSION',
            datasourceSqlMap: BIVizDashboardSales.sqlMap,
            filterContext: true,
            orderByQuery: datasourceColumnOrderByQuery(
                'FIREBASE_INSTALL_VERSION'
            ),
        }).create(),
        'mopa.sales.filter.firebase_install_version_group_minor':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'mopa.sales.filter.firebase_install_version_group_minor',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'FIREBASE_INSTALL_VERSION_GROUP_MINOR',
                datasourceSqlMap: BIVizDashboardSales.sqlMap,
                filterContext: true,
                orderByQuery: datasourceColumnOrderByQuery(
                    'FIREBASE_INSTALL_VERSION_GROUP_MINOR'
                ),
            }).create(),
        'mopa.sales.filter.etl_payer': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.sales.filter.etl_payer',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'ETL_PAYER',
            datasourceSqlMap: BIVizDashboardSales.sqlMap,
        }).create(),
        'mopa.sales.filter.etl_revenue_badge': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.sales.filter.etl_revenue_badge',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'ETL_REVENUE_BADGE',
            datasourceSqlMap: BIVizDashboardSales.sqlMap,
            orderByQuery: datasourceColumnOrderByQuery('ETL_REVENUE_BADGE'),
        }).create(),
        'mopa.sales.filter.attribution_network_name': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.sales.filter.attribution_network_name',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'ATTRIBUTION_NETWORK_NAME',
            datasourceSqlMap: BIVizDashboardSales.sqlMap,
        }).create(),
        'mopa.sales.filter.attribution_campaign_name': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.sales.filter.attribution_campaign_name',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'ATTRIBUTION_CAMPAIGN_NAME',
            datasourceSqlMap: BIVizDashboardSales.sqlMap,
            filterContext: true,
        }).create(),
    };

    override chartDataLoaders = {
        'mopa.sales.modify_time_sales_datasource': DataLoaderFactories[
            'common.modify_time'
        ]({
            createdInstanceId: 'mopa.sales.modify_time_sales_datasource',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasetId: 'bi_viz',
            tableId: 'dashboard_sales',
        }).create(),
        'mopa.sales.revenue_agg_dau': new RevenueDauDataLoaderFactory({
            createdInstanceId: 'mopa.sales.revenue_agg_dau',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
        }).create(),
        'mopa.sales.revenue_arpu': new RevenueARPUDataLoaderFactory({
            createdInstanceId: 'mopa.sales.revenue_arpu',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
        }).create(),
        'mopa.sales.sales_comparison': new SalesComparisonDataLoaderFactory({
            createdInstanceId: 'mopa.sales.sales_comparison',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
        }).create(),
    };

    /////////////////////////////////////////////////////////////////
    ////
    ////    [DASHBOARD ITEM <-> DATALOADER] LINKS
    ////
    /////////////////////////////////////////////////////////////////
    settingItemDateAggregation: ISingleSelectSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.DATE_AGGREGATION,
            viewtype: 'singleSelect',
            displayName: 'Date aggregation',
            value: 'day' as datePartDateTrunc,
            selectOptions: getAllDatePartDateTrunc(),
        },
    };

    settingItemNetGross: IToggleSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.NET_GROSS,
            viewtype: 'toggle',
            value: 'net',
            selectOptions: ['net', 'gross'],
        },
    };
    settingItemCurrency: IToggleSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.CURRENCY,
            viewtype: 'toggle',
            value: 'pln',
            selectOptions: ['pln', 'usd'],
        },
    };
    settingItemShowTotalRevenue: ICheckboxSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.SHOW_TOTAL_REVENUE,
            viewtype: 'checkbox',
            displayName: 'total',
            value: true,
        },
    };
    settingItemShowInappRevenue: ICheckboxSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.SHOW_INAPP_REVENUE,
            viewtype: 'checkbox',
            displayName: 'inapp',
        },
    };
    settingItemShowAdRevenue: ICheckboxSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.SHOW_AD_REVENUE,
            viewtype: 'checkbox',
            displayName: 'ad',
        },
    };

    // CHARTS
    modify_time_sales_datasource: IDashboardItemDataLoaderLink = {
        id: 'mopa.sales.modify_time_sales_datasource',
        dashboardItemFactory: DashboardItemFactories['common.modify_time'](
            new ModifyTimeDashboardItemFactoryOptions(
                'mopa.sales.modify_time_sales_datasource',
                'value'
            )
        ),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.sales.modify_time_sales_datasource']
                .emitter,
    };

    arpu: IDashboardItemDataLoaderLink = {
        id: 'mopa.sales.chart.arpu',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            createdInstanceId: 'mopa.sales.chart.arpu',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            dashboardItem: {
                title: 'Running ARPU',
                subtitle:
                    'Ignored filters: Activity Date, Activity App Version.',
                chartDocumentation: MopaSalesChartDocs.arpu,
            },
            dashboardName: this.DASHBOARD_NAME,
            metricsGetter: EChartsHelper.createRevenueMetricsGetter({
                metricId: 'ARPU',
                metricName: 'Running ARPU',
            }),
            xAxisType: 'value',
            xColumnLabel: 'day_no',
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) => `Day no: ${label}`;
                },

                valueFormatterGetter(context) {
                    return (v: any) =>
                        EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                            v,
                            context.settings.currency || 'pln'
                        );
                },
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                // echartsOption.tooltip =
                //     EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                //         xLabelFormatter: (label: any) => `Day no: ${label}`,
                //         valueFormatter: (v) =>
                //             EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                //                 v,
                //                 context.settings.currency || 'pln'
                //             ),
                //     });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.sales.revenue_arpu'].emitter,
    };
    r_rev_perc_total: IDashboardItemDataLoaderLink = {
        id: 'mopa.sales.chart.r_rev_perc_total',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            createdInstanceId: 'mopa.sales.chart.r_rev_perc_total',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            dashboardItem: {
                title: 'Running % of total revenue',
                subtitle:
                    'Ignored filters: Activity Date, Activity App Version.',
                chartDocumentation: MopaSalesChartDocs.r_rev_perc_total,
            },
            dashboardName: this.DASHBOARD_NAME,
            metricsGetter: EChartsHelper.createRevenueMetricsGetter({
                metricId: 'running_p_total_revenue',
                metricName: 'Running % of revenue',
            }),
            xAxisType: 'value',
            xColumnLabel: 'day_no',
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) => `Day no: ${label}`;
                },
                valueFormatterGetter(context) {
                    return (v: any) =>
                        EChartsTooltipHelper.tooltipPercentValueFormatter(v);
                },
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter:
                            EChartsAxisHelper.getAxisPercentLabelFormatter(0),
                    },
                };
                // echartsOption.tooltip =
                //     EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                //         xLabelFormatter: (label: any) => `Day no: ${label}`,
                //         valueFormatter:
                //             EChartsTooltipHelper.tooltipPercentValueFormatter,
                //     });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.sales.revenue_arpu'].emitter,
    };
    revenue: IDashboardItemDataLoaderLink = {
        id: 'mopa.sales.chart.revenue',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            createdInstanceId: 'mopa.sales.chart.revenue',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            dashboardItem: {
                title: 'Revenue',
                chartDocumentation: MopaSalesChartDocs.revenue,
            },
            dashboardName: this.DASHBOARD_NAME,
            xColumnLabel: 'date',
            metricsGetter: EChartsHelper.createRevenueMetricsGetter({
                metricId: 'revenue',
                metricName: 'Revenue',
            }),
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (v: any) =>
                        EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                            v,
                            context.settings.currency || 'pln'
                        );
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.sales.revenue_agg_dau'].emitter,
    };
    new_payers: IDashboardItemDataLoaderLink = {
        id: 'mopa.sales.chart.new_payers',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            createdInstanceId: 'mopa.sales.chart.new_payers',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            dashboardItem: {
                title: 'New payers',
                chartDocumentation: MopaSalesChartDocs.new_payers,
            },
            dashboardName: this.DASHBOARD_NAME,
            xColumnLabel: 'date',
            metricName: 'New payers',
            yColumnLabel: 'cnt_first_transaction',
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return EChartsTooltipHelper.tooltipIntegerValueFormatter;
                },
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                    },
                };
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.sales.revenue_agg_dau'].emitter,
    };
    transactions: IDashboardItemDataLoaderLink = {
        id: 'mopa.sales.chart.transactions',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            createdInstanceId: 'mopa.sales.chart.transactions',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            dashboardItem: {
                title: 'Transactions',
                chartDocumentation: MopaSalesChartDocs.transactions,
            },
            dashboardName: this.DASHBOARD_NAME,
            xColumnLabel: 'date',
            metricName: 'Transactions',
            yColumnLabel: 'transactions',
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return EChartsTooltipHelper.tooltipIntegerValueFormatter;
                },
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                    },
                };
                // echartsOption.tooltip =
                //     EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                //         xLabelFormatter: (label: any) =>
                //             EChartsAxisHelper.axisLabelDateFormatter(
                //                 label,
                //                 context
                //             ),
                //         valueFormatter:
                //             EChartsTooltipHelper.tooltipIntegerValueFormatter,
                //     });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.sales.revenue_agg_dau'].emitter,
    };
    wavg_arpdau: IDashboardItemDataLoaderLink = {
        id: 'mopa.sales.chart.wavg_arpdau',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            createdInstanceId: 'mopa.sales.chart.wavg_arpdau',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            dashboardItem: {
                title: 'ARPDAU',
                chartDocumentation: MopaSalesChartDocs.wavg_arpdau,
            },
            dashboardName: this.DASHBOARD_NAME,
            xColumnLabel: 'date',
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (v: any) =>
                        EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                            v,
                            context.settings.currency || 'pln'
                        );
                },
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                // echartsOption.tooltip =
                //     EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                //         xLabelFormatter: (label: any) =>
                //             EChartsAxisHelper.axisLabelDateFormatter(
                //                 label,
                //                 context
                //             ),
                //         valueFormatter: (v) =>
                //             EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                //                 v,
                //                 context.settings.currency || 'pln'
                //             ),
                //     });
            },
            metricsGetter: EChartsHelper.createRevenueMetricsGetter({
                metricId: 'wavg_ARPDAU',
                metricName: 'ARPDAU',
            }),
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.sales.revenue_agg_dau'].emitter,
    };
    wavg_cr: IDashboardItemDataLoaderLink = {
        id: 'mopa.sales.chart.wavg_cr',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            createdInstanceId: 'mopa.sales.chart.wavg_cr',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            dashboardItem: {
                title: 'Conversion rate',
                subtitle: 'Rate of players who made any purchase',
                chartDocumentation: MopaSalesChartDocs.wavg_cr,
            },
            dashboardName: this.DASHBOARD_NAME,
            echartsSeriesType: 'line',
            xColumnLabel: 'date',
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return EChartsTooltipHelper.tooltipPercentValueFormatter;
                },
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter:
                            EChartsAxisHelper.getAxisPercentLabelFormatter(0),
                    },
                };
                // echartsOption.tooltip =
                //     EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                //         xLabelFormatter: (label: any) =>
                //             EChartsAxisHelper.axisLabelDateFormatter(
                //                 label,
                //                 context
                //             ),
                //         valueFormatter:
                //             EChartsTooltipHelper.tooltipPercentValueFormatter,
                //     });
            },
            yColumnLabel: 'wavg_conversion_rate',
            metricName: 'Conversion rate',
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.sales.revenue_agg_dau'].emitter,
    };
    comparison_transactions: IDashboardItemDataLoaderLink = {
        id: 'mopa.sales.chart.comparison_transactions',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'sales',
            createdInstanceId: 'mopa.sales.chart.comparison_transactions',
            dashboardItem: {
                title: 'Transactions count',
                subtitle:
                    'Absolute difference calculated as am_bi - store_report',
                chartDocumentation: MopaSalesChartDocs.comparison,
            },
            xColumnLabel: 'date',
            yColumnLabel: 'transactions_cnt_abs_diff',
            metricName: 'Transactions count absolute difference',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return EChartsTooltipHelper.tooltipIntegerValueFormatter;
                },
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                    },
                };
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.sales.sales_comparison'].emitter,
    };
    comparison_revenue: IDashboardItemDataLoaderLink = {
        id: 'mopa.sales.chart.comparison_revenue',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'sales',
            createdInstanceId: 'mopa.sales.chart.comparison_revenue',
            dashboardItem: {
                title: 'Revenue',
                subtitle:
                    'Absolute difference calculated as am_bi - store_report',
                chartDocumentation: MopaSalesChartDocs.comparison,
            },
            xColumnLabel: 'date',
            yColumnLabel: (context: IQueryContext) => {
                const netGross =
                    context.settings.netGross == 'net' ? 'net_' : 'gross_';
                return `revenue_${netGross}${context.settings.currency}_abs_diff`;
            },
            metricName: (context) => {
                const netGross =
                    context.settings.netGross == 'net' ? ' net' : ' gross';
                return `Revenue ${netGross} absolute difference`;
            },
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter:
                            EChartsAxisHelper.axisLabelDateFormatter,
                        valueFormatter: (v) =>
                            EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                                v,
                                context.settings.currency || 'pln'
                            ),
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.sales.sales_comparison'].emitter,
    };
    invalid_transactions: IDashboardItemDataLoaderLink = {
        id: 'mopa.sales.chart.invalid_transactions',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'sales',
            createdInstanceId: 'mopa.sales.chart.invalid_transactions',
            dashboardItem: {
                title: 'Invalid transactions count',
                subtitle:
                    'Count of invalid event transactions (e.g. unverified, repeated)',
                chartDocumentation: MopaSalesChartDocs.invalid_transactions,
            },
            xColumnLabel: 'date',
            yColumnLabel: 'invalid_transactions_cnt',
            metricName: 'Invalid transactions count',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter:
                            EChartsAxisHelper.axisLabelDateFormatter,
                        valueFormatter:
                            EChartsTooltipHelper.tooltipIntegerValueFormatter,
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.sales.sales_comparison'].emitter,
    };

    // FILTERS
    filterDashboardItemLinks: Partial<
        Record<DatasourceColumnId, IDashboardItemDataLoaderLink>
    > = {
        SALES_OFFER_TYPE: {
            id: 'mopa.sales.filter.sales_offer_type',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId: 'mopa.sales.filter.sales_offer_type',
                datasourceColumnId: 'SALES_OFFER_TYPE',
            }),
            dataLoaderEmitter:
                this.filterDataLoaders['mopa.sales.filter.offer_type'].emitter,
        },
        SALES_OFFER_SEGMENT: {
            id: 'mopa.sales.filter.sales_offer_segment',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId: 'mopa.sales.filter.sales_offer_segment',
                datasourceColumnId: 'SALES_OFFER_SEGMENT',
            }),
            dataLoaderEmitter:
                this.filterDataLoaders['mopa.sales.filter.offer_segment']
                    .emitter,
        },
        SALES_OFFER_SEASON_SIZE: {
            id: 'mopa.sales.filter.sales_offer_season_size',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId: 'mopa.sales.filter.sales_offer_season_size',
                datasourceColumnId: 'SALES_OFFER_SEASON_SIZE',
            }),
            dataLoaderEmitter:
                this.filterDataLoaders['mopa.sales.filter.offer_season_size']
                    .emitter,
        },
        SALES_OFFER_SEASON_VARIANT: {
            id: 'mopa.sales.filter.sales_offer_season_variant',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId:
                    'mopa.sales.filter.sales_offer_season_variant',
                datasourceColumnId: 'SALES_OFFER_SEASON_VARIANT',
            }),
            dataLoaderEmitter:
                this.filterDataLoaders['mopa.sales.filter.offer_season_variant']
                    .emitter,
        },
        SALES_OFFER_NAME: {
            id: 'mopa.sales.filter.sales_offer_name',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId: 'mopa.sales.filter.sales_offer_name',
                datasourceColumnId: 'SALES_OFFER_NAME',
            }),
            dataLoaderEmitter:
                this.filterDataLoaders['mopa.sales.filter.offer_name'].emitter,
        },
        SALES_INAPP_SOURCE: {
            id: 'mopa.sales.filter.sales_inapp_source',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId: 'mopa.sales.filter.sales_inapp_source',
                datasourceColumnId: 'SALES_INAPP_SOURCE',
            }),
            dataLoaderEmitter:
                this.filterDataLoaders['mopa.sales.filter.inapp_source']
                    .emitter,
        },
        SALES_INAPP_PRODUCT_ID: {
            id: 'mopa.sales.filter.sales_inapp_product_id',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId: 'mopa.sales.filter.sales_inapp_product_id',
                datasourceColumnId: 'SALES_INAPP_PRODUCT_ID',
            }),
            dataLoaderEmitter:
                this.filterDataLoaders['mopa.sales.filter.inapp_product_id']
                    .emitter,
        },
        SALES_INAPP_PRODUCT_ID_PRICE_TIER: {
            id: 'mopa.sales.filter.sales_inapp_product_id_price_tier',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId:
                    'mopa.sales.filter.sales_inapp_product_id_price_tier',
                datasourceColumnId: 'SALES_INAPP_PRODUCT_ID_PRICE_TIER',
            }),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.sales.filter.inapp_product_id_price_tier'
                ].emitter,
        },
        SALES_INAPP_TRANSACTION_NO_BUCKET: {
            id: 'mopa.sales.filter.sales_inapp_transactions_no_bucket',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId:
                    'mopa.sales.filter.sales_inapp_transactions_no_bucket',
                datasourceColumnId: 'SALES_INAPP_TRANSACTION_NO_BUCKET',
            }),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.sales.filter.sales_inapp_transactions_no_bucket'
                ].emitter,
        },
        SALES_INAPP_TRANSACTION_NO_LABEL: {
            id: 'mopa.sales.filter.sales_inapp_transactions_no_label',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId:
                    'mopa.sales.filter.sales_inapp_transactions_no_label',
                datasourceColumnId: 'SALES_INAPP_TRANSACTION_NO_LABEL',
            }),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.sales.filter.sales_inapp_transactions_no_label'
                ].emitter,
        },
        AD_PLATFORM: {
            id: 'mopa.sales.filter.ad_platform',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId: 'mopa.sales.filter.ad_platform',
                datasourceColumnId: 'AD_PLATFORM',
            }),
            dataLoaderEmitter:
                this.filterDataLoaders['mopa.sales.filter.ad_platform'].emitter,
        },
        ACTIVITY_DATE: {
            id: 'mopa.sales.filter.activity_date',
            dashboardItemFactory: DashboardItemFactories['filter.daterange']({
                createdInstanceId: 'mopa.sales.filter.activity_date',
                datasourceColumnId: 'ACTIVITY_DATE',
            }),
            // empty emitter DataRangePicker doesn't need data
            dataLoaderEmitter: new EventEmitter<IDataLoaderResult>(),
        },
        ACTIVITY_APP_VERSION_GROUP_MINOR: {
            id: 'mopa.sales.filter.app_version_group_minor',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId: 'mopa.sales.filter.app_version_group_minor',
                datasourceColumnId: 'ACTIVITY_APP_VERSION_GROUP_MINOR',
            }),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.sales.filter.activity_app_version_group_minor'
                ].emitter,
        },
        ACTIVITY_APP_VERSION: {
            id: 'mopa.sales.filter.app_version',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId: 'mopa.sales.filter.app_version',
                datasourceColumnId: 'ACTIVITY_APP_VERSION',
            }),
            dataLoaderEmitter:
                this.filterDataLoaders['mopa.sales.filter.activity_app_version']
                    .emitter,
        },
        ACTIVITY_DAY_NO: {
            id: 'mopa.sales.filter.activity_day_no',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId: 'mopa.sales.filter.activity_day_no',
                datasourceColumnId: 'ACTIVITY_DAY_NO',
            }),
            dataLoaderEmitter:
                this.filterDataLoaders['mopa.sales.filter.activity_day_no']
                    .emitter,
        },
        DAILY_TOTAL_ENERGY_SPENT: {
            id: 'mopa.sales.filter.daily_total_energy_spent',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId: 'mopa.sales.filter.daily_total_energy_spent',
                datasourceColumnId: 'DAILY_TOTAL_ENERGY_SPENT',
            }),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.sales.filter.daily_total_energy_spent'
                ].emitter,
        },
        SEGMENT: {
            id: 'mopa.sales.filter.segments',
            dashboardItemFactory: DashboardItemFactories['filter.segments']({
                createdInstanceId: 'mopa.sales.filter.segments',
            }),
            // empty emitter DataRangePicker doesn't need data
            dataLoaderEmitter:
                this.filterDataLoaders['mopa.sales.filter.segment'].emitter,
        },
        FIREBASE_FIRST_OPEN_DATE: {
            id: 'mopa.sales.filter.firebase_first_open_date',
            dashboardItemFactory: DashboardItemFactories['filter.daterange']({
                createdInstanceId: 'mopa.sales.filter.firebase_first_open_date',
                datasourceColumnId: 'FIREBASE_FIRST_OPEN_DATE',
                splitable: true,
            }),
            // empty emitter DataRangePicker doesn't need data
            dataLoaderEmitter: new EventEmitter<IDataLoaderResult>(),
        },
        FIREBASE_APP_STORE: {
            id: 'mopa.sales.filter.firebase_app_store',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId: 'mopa.sales.filter.firebase_app_store',
                datasourceColumnId: 'FIREBASE_APP_STORE',
            }),
            dataLoaderEmitter:
                this.filterDataLoaders['mopa.sales.filter.firebase_app_store']
                    .emitter,
        },
        FIREBASE_COUNTRY_EUROPEAN_UNION: {
            id: 'mopa.sales.filter.firebase_country_european_union',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId:
                    'mopa.sales.filter.firebase_country_european_union',
                datasourceColumnId: 'FIREBASE_COUNTRY_EUROPEAN_UNION',
            }),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.sales.filter.firebase_country_european_union'
                ].emitter,
        },
        FIREBASE_COUNTRY_TIER: {
            id: 'mopa.sales.filter.firebase_country_tier',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId: 'mopa.sales.filter.firebase_country_tier',
                datasourceColumnId: 'FIREBASE_COUNTRY_TIER',
            }),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.sales.filter.firebase_country_tier'
                ].emitter,
        },
        FIREBASE_COUNTRY_IS_RELEVANT_LABEL: {
            id: 'mopa.sales.filter.firebase_country_is_relevant_label',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId:
                    'mopa.sales.filter.firebase_country_is_relevant_label',
                datasourceColumnId: 'FIREBASE_COUNTRY_IS_RELEVANT_LABEL',
            }),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.sales.filter.firebase_country_is_relevant_label'
                ].emitter,
        },
        FIREBASE_COUNTRY: {
            id: 'mopa.sales.filter.firebase_country',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId: 'mopa.sales.filter.firebase_country',
                datasourceColumnId: 'FIREBASE_COUNTRY',
            }),
            dataLoaderEmitter:
                this.filterDataLoaders['mopa.sales.filter.firebase_country']
                    .emitter,
        },
        FIREBASE_COUNTRY_US_REGION: {
            id: 'mopa.sales.filter.firebase_country_us_region',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId:
                    'mopa.sales.filter.firebase_country_us_region',
                datasourceColumnId: 'FIREBASE_COUNTRY_US_REGION',
            }),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.sales.filter.firebase_country_us_region'
                ].emitter,
        },
        ATTRIBUTION_TRAFFIC_SOURCE: {
            id: 'mopa.sales.filter.attribution_traffic_source',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId:
                    'mopa.sales.filter.attribution_traffic_source',
                datasourceColumnId: 'ATTRIBUTION_TRAFFIC_SOURCE',
            }),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.sales.filter.attribution_traffic_source'
                ].emitter,
        },
        ATTRIBUTION_NETWORK_NAME: {
            id: 'mopa.sales.filter.attribution_network_name',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId: 'mopa.sales.filter.attribution_network_name',
                datasourceColumnId: 'ATTRIBUTION_NETWORK_NAME',
            }),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.sales.filter.attribution_network_name'
                ].emitter,
        },
        FIREBASE_INSTALL_VERSION_GROUP_MINOR: {
            id: 'mopa.sales.filter.firebase_install_version_group_minor',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId:
                    'mopa.sales.filter.firebase_install_version_group_minor',
                datasourceColumnId: 'FIREBASE_INSTALL_VERSION_GROUP_MINOR',
            }),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.sales.filter.firebase_install_version_group_minor'
                ].emitter,
        },
        FIREBASE_INSTALL_VERSION_ALIAS: {
            id: 'mopa.sales.filter.firebase_install_version',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId: 'mopa.sales.filter.firebase_install_version',
                datasourceColumnId: 'FIREBASE_INSTALL_VERSION',
            }),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.sales.filter.firebase_install_version'
                ].emitter,
        },
        ETL_PAYER: {
            id: 'mopa.sales.filter.etl_payer',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId: 'mopa.sales.filter.etl_payer',
                datasourceColumnId: 'ETL_PAYER',
            }),
            dataLoaderEmitter:
                this.filterDataLoaders['mopa.sales.filter.etl_payer'].emitter,
        },
        ETL_REVENUE_BADGE: {
            id: 'mopa.sales.filter.etl_revenue_badge',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId: 'mopa.sales.filter.etl_revenue_badge',
                datasourceColumnId: 'ETL_REVENUE_BADGE',
            }),
            dataLoaderEmitter:
                this.filterDataLoaders['mopa.sales.filter.etl_revenue_badge']
                    .emitter,
        },
        ATTRIBUTION_CAMPAIGN_NAME: {
            id: 'mopa.sales.filter.attribution_campaign_name',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId:
                    'mopa.sales.filter.attribution_campaign_name',
                datasourceColumnId: 'ATTRIBUTION_CAMPAIGN_NAME',
            }),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.sales.filter.attribution_campaign_name'
                ].emitter,
        },
    };

    override async ngOnInit() {
        super.ngOnInit();
        console.log({
            msg: 'SalesComponent.ngOnInit',
            cancellationToken: this.cancellationToken,
        });
    }

    async ngAfterViewInit() {
        console.log({
            msg: 'SalesComponent.ngAfterViewInit',
            cancellationToken: this.cancellationToken,
        });
        await this.standardDashboardInitialize();
    }

    override getInitialContext(): IQueryContext {
        const initialContext = QueryContextService.INITIAL_CONTEXT;

        const firebaseAppStoreIC: IMultipleSelectDashboardItem['datasourceColumn'] =
            {
                id: 'FIREBASE_APP_STORE',
                state: {
                    value: {
                        options: [
                            'Amazon Appstore',
                            'Google Play',
                            'Microsoft Store',
                            'iTunes',
                            'iTunes macOS',
                        ],
                        filterType: 'include',
                    },
                    isFilterApplied: true,
                    isSplitApplied: false,
                },
            };
        const firebaseCountryTierIC: IMultipleSelectDashboardItem['datasourceColumn'] =
            {
                id: 'FIREBASE_COUNTRY_TIER',
                state: {
                    value: {
                        options: ['Tier 1', 'Tier 2', 'Tier 3'],
                        filterType: 'include',
                    },
                    // filterType: 'include',
                    isFilterApplied: true,
                    isSplitApplied: false,
                },
            };
        const activityDateIC: IDateRangePickerDashboardItem['datasourceColumn'] =
            {
                id: 'ACTIVITY_DATE',
                state: {
                    value: {
                        preset: 'last 365 days',
                        splitAggregate: 'day',
                    },
                    isFilterApplied: true,
                    isSplitApplied: false,
                },
            };

        // set initialContext for AppStore to Google Play
        initialContext.datasourceColumns = [
            // firebaseAppStoreIC,
            // firebaseCountryTierIC,
            activityDateIC,
        ];

        return initialContext;
    }
}
